import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useLogout } from '../../hooks/api/useAuth';

/**
 * The signed-in name chip, turned into a small dropdown so there is a way out.
 *
 * One component behind three surfaces (account shell, admin bar, public nav):
 * `useLogout` was written but never called, so nobody could sign out. On logout
 * the stored tokens are dropped and the browser lands on `/`; navigating (not a
 * hard reload) is enough because the header re-reads auth state on every route
 * change.
 *
 * The trigger's look is passed in — each surface styles its own chip — while the
 * popover, keyboard handling and the sign-out action live here once.
 */
interface UserMenuProps {
  /** The chip content shown on the trigger button (name, avatar, etc.). */
  trigger: ReactNode;
  /** Which edge the popover aligns to. */
  align?: 'left' | 'right';
  /** Optional "Akun saya" link (public nav, where the name used to link there). */
  accountHref?: string;
  /** Extra classes for the trigger button, so the chip keeps its surface look. */
  triggerClassName?: string;
  /** Extra classes for the wrapper (e.g. flex `order` on the header row). */
  className?: string;
}

export function UserMenu({
  trigger,
  align = 'right',
  accountHref,
  triggerClassName = '',
  className = '',
}: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const logout = useLogout();
  const navigate = useNavigate();

  // Close on outside click and on Escape — a dropdown that only closes by
  // re-clicking its trigger traps keyboard users.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const handleLogout = async () => {
    await logout.mutateAsync();
    setOpen(false);
    navigate('/');
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={`focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink ${triggerClassName}`}
      >
        {trigger}
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute top-full z-50 mt-2 min-w-[188px] rounded-block border-2 border-line bg-white p-2 ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {accountHref && (
            <Link
              role="menuitem"
              to={accountHref}
              onClick={() => setOpen(false)}
              className="flex min-h-[44px] items-center rounded-block px-3 py-2 text-[14.5px] font-bold text-ink no-underline transition-colors duration-150 ease-out hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink"
            >
              Akun saya
            </Link>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            disabled={logout.isPending}
            className="flex min-h-[44px] w-full items-center gap-2 rounded-block px-3 py-2 text-left text-[14.5px] font-bold text-ink transition-colors duration-150 ease-out hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink disabled:opacity-60"
          >
            <LogOut size={16} aria-hidden="true" />
            {logout.isPending ? 'Keluar…' : 'Keluar'}
          </button>
        </div>
      )}
    </div>
  );
}

export default UserMenu;
